/**
 * Minimal demo analytics. The demo stubs out the app's analytics stack, so this posts directly
 * to the same ingest endpoint the prod app feeds (POST /trackevents/ → Segment).
 *
 * Identity: `anonymous_id` is `cltv_anonymous_id`, the cross-subdomain visitor id www's
 * tracking.js mints and the funnel sends to the backend as `lead_ids`. Sharing that id is what
 * lets a demo visit be joined to the signup it leads to. `demo_session_id` is the per-page-load id
 * this module used to send as `anonymous_id`, kept as an event property so visit counts survive
 * `anonymous_id` becoming a per-person id.
 */
import { EntityType } from './types';

// `/trackevents/` is at the Django root, not under the proxied `/api/public/*` prefix — needs its
// own proxy rule or a direct URL via VITE_DEMO_TRACK_EVENTS_URL.
const TRACK_EVENTS_URL = (import.meta.env.VITE_DEMO_TRACK_EVENTS_URL as string | undefined) || '/trackevents/';

const TRACKING_COOKIE_NAME = 'cltv_anonymous_id';
// One year, matching tracking.js.
const TRACKING_COOKIE_MAX_AGE_SECONDS = 31536000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type DemoEventName =
    | 'Demo Page Viewed'
    | 'Demo Expired Page Viewed'
    | 'Demo Tile Interacted'
    | 'Demo CTA Clicked'
    | 'Demo Session Ended'
    | 'Demo Control Changed';

/**
 * We read and write `cltv_anonymous_id` here rather than through `modules/common/utils`. That
 * shared helper hardcodes `samesite=Lax`, emits no `Secure`, and url-encodes the value — three
 * independent ways to diverge from what tracking.js writes.
 *
 * Divergence is not cosmetic: cookie identity is (name, Domain, Path), so a mismatched Domain does
 * not update www's cookie, it adds a second one of the same name that JS cannot tell apart, and
 * the visitor's identity splits permanently.
 */
function readTrackingCookie(): string | undefined {
    const match = new RegExp(`(?:^|;\\s*)${TRACKING_COOKIE_NAME}=([^;]*)`).exec(document.cookie);
    return match ? match[1] : undefined;
}

// The registrable domain, derived the way tracking.js does: collective.com from
// demo.collective.com, eng-collective.com from demo-ca.eng-collective.com. The demo bundle is
// environment-agnostic (one build serves both), so this cannot come from an env var. Undefined on
// a single-label host such as localhost, where a host-only cookie is what we want.
function trackingCookieDomain(): string | undefined {
    const parts = window.location.hostname.split('.');
    return parts.length > 1 ? parts.slice(-2).join('.') : undefined;
}

function writeTrackingCookie(value: string): void {
    const domain = trackingCookieDomain();
    const domainAttr = domain ? `; Domain=.${domain}` : '';
    // `SameSite=None` requires `Secure`, and the funnel's cross-site POST needs SameSite=None.
    // Browsers treat localhost as a secure context, so this still works in dev over http.
    document.cookie =
        `${TRACKING_COOKIE_NAME}=${value}${domainAttr}` +
        `; Path=/; Max-Age=${TRACKING_COOKIE_MAX_AGE_SECONDS}; SameSite=None; Secure`;
}

let anonymousId: string | undefined;

/**
 * The visitor's cross-subdomain id: whatever app-demo echoed on the profile response, else the
 * cookie www may have written, else a fresh one we persist so the funnel picks it up later.
 *
 * A cookie value that is not a canonical UUID is discarded rather than forwarded. The backend's
 * `/trackevents/` intake rejects a non-UUID `anonymous_id` with a 400, and this module swallows
 * transport errors, so passing one through would silently drop every demo event for that visitor.
 */
export function getAnonymousId(): string {
    if (!anonymousId) {
        const fromCookie = readTrackingCookie();
        if (fromCookie && UUID_RE.test(fromCookie)) {
            anonymousId = fromCookie;
        } else {
            anonymousId = crypto.randomUUID();
            writeTrackingCookie(anonymousId);
        }
    }
    return anonymousId;
}

// One id per page load — what this module used to send as `anonymous_id`.
// NOT sent as `session_id`: Segment's Amplitude destination treats that as a reserved field
// and maps it onto Amplitude's own session id, which must be a numeric epoch-ms long. A UUID
// there makes Amplitude reject the whole event at ingestion — Django still returns 204 and the
// browser sees nothing wrong, so the events just vanish.
let sessionId: string | undefined;

function getSessionId(): string {
    if (!sessionId) {
        sessionId = crypto.randomUUID();
    }
    return sessionId;
}

interface DemoAnalyticsContext {
    entityType?: EntityType;
    demoToken?: string;
    /** The funnel lead the demo link was minted for; absent for lead-less links. */
    leadUuid?: string | null;
    /** The id app-demo resolved for this visitor and set as a cookie on the same response. */
    anonymousId?: string;
}

// Set once at boot so every event carries this without threading it through call sites.
let analyticsContext: DemoAnalyticsContext = {};

export function setDemoAnalyticsContext(next: DemoAnalyticsContext): void {
    analyticsContext = next;
    // Adopt the server's value without rewriting the cookie: app-demo already set it on the same
    // response, and a server-set cookie outlives a JS-set one, which Safari's ITP caps at 7 days.
    if (next.anonymousId && UUID_RE.test(next.anonymousId)) {
        anonymousId = next.anonymousId;
    }
}

export function trackDemoEvent(
    name: DemoEventName,
    properties: Record<string, unknown> = {},
    { beacon = false }: { beacon?: boolean } = {}
): void {
    try {
        // Strip the reserved key even if a caller supplies it: `session_id` is bound to
        // Amplitude's own session field, and a non-numeric value there makes Amplitude drop the
        // whole event silently. The module never sets it, but nothing in the type system stops a
        // call site from doing so, and the failure is invisible from the browser.
        const { session_id: reservedSessionId, ...callerProperties } = properties;

        const body = JSON.stringify({
            event_type: 'track',
            name,
            anonymous_id: getAnonymousId(),
            context: {
                page: {
                    path: window.location.pathname,
                    url: window.location.href,
                    referrer: document.referrer,
                },
            },
            properties: {
                ...callerProperties,
                entity_type: analyticsContext.entityType,
                demo_session_id: getSessionId(),
                demo_token: analyticsContext.demoToken,
                lead_uuid: analyticsContext.leadUuid ?? undefined,
            },
        });

        // sendBeacon for unload/navigation paths — a normal fetch gets cancelled.
        // It is no-cors and this is a non-simple content type, so it reports failure by returning
        // false rather than throwing; fall through to the keepalive fetch instead of dropping.
        if (beacon && navigator.sendBeacon?.(TRACK_EVENTS_URL, new Blob([body], { type: 'application/json' }))) {
            return;
        }

        // Best-effort — never break the demo on a failed analytics call.
        // same-origin (not omit) so the Cloudflare Access session cookie rides along; without it
        // Access 302s the request to its login page. (sendBeacon above sends cookies by default.)
        fetch(TRACK_EVENTS_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            credentials: 'same-origin',
            keepalive: true,
        }).catch(() => {});
    } catch {
        // Never let tracking throw into the UI.
    }
}
